/**
 * Proof attached to a bug report: validate → presign → direct S3 PUT, with a
 * per-file progress bar. Same status/progress item shape as
 * `useEmailAttachmentUpload`, so `report-bug-form` renders the identical
 * uploading chip the email composer does.
 *
 * Two-step rather than the tickets three-step: there is no report to confirm
 * the upload against yet, so the metadata row is written when the report itself
 * is created. Until then the object just sits under the caller's draft prefix.
 */
import { useCallback, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { bugReportsApi } from '@/api/services/bug-reports.api';
import {
    MAX_BUG_REPORT_ATTACHMENTS,
    MAX_BUG_REPORT_ATTACHMENT_SIZE_BYTES,
    MAX_BUG_REPORT_ATTACHMENT_SIZE_MB,
} from '../constants';
import type { IBugReportAttachmentInput } from '../types';

const FALLBACK_CONTENT_TYPE = 'application/octet-stream';

export type BugReportUploadStatus = 'uploading' | 'done' | 'error';

export interface IPendingBugReportAttachment {
    readonly localId: string;
    readonly fileName: string;
    readonly contentType: string;
    readonly sizeBytes: number;
    readonly status: BugReportUploadStatus;
    /** 0–100, driven by the S3 PUT's own progress events. */
    readonly progress: number;
    /** Set once the S3 PUT completes. */
    readonly storageKey?: string;
}

export interface IUseBugReportAttachmentUploadResult {
    readonly items: readonly IPendingBugReportAttachment[];
    /** Only the files that actually landed — what the report will claim. */
    readonly uploaded: readonly IBugReportAttachmentInput[];
    readonly isUploading: boolean;
    addFiles(files: FileList | File[]): void;
    remove(localId: string): void;
    clear(): void;
}

export function useBugReportAttachmentUpload(): IUseBugReportAttachmentUploadResult {
    const { t } = useTranslation();
    const [items, setItems] = useState<IPendingBugReportAttachment[]>([]);

    const uploadOne = useCallback(
        async (localId: string, file: File) => {
            const contentType = file.type || FALLBACK_CONTENT_TYPE;
            try {
                const { uploadUrl, storageKey } = await bugReportsApi.getUploadUrl({
                    fileName: file.name,
                    contentType,
                    sizeBytes: file.size,
                });
                // Bare `axios`, not `apiClient`: the presigned URL carries its
                // own auth in the query string and an Authorization header
                // would break the signature.
                await axios.put(uploadUrl, file, {
                    headers: { 'Content-Type': contentType },
                    onUploadProgress: (event) => {
                        const progress = event.total
                            ? Math.round((event.loaded / event.total) * 100)
                            : 0;
                        setItems((prev) =>
                            prev.map((item) =>
                                item.localId === localId ? { ...item, progress } : item,
                            ),
                        );
                    },
                });
                setItems((prev) =>
                    prev.map((item) =>
                        item.localId === localId
                            ? { ...item, status: 'done', progress: 100, storageKey }
                            : item,
                    ),
                );
            } catch {
                setItems((prev) =>
                    prev.map((item) =>
                        item.localId === localId ? { ...item, status: 'error' } : item,
                    ),
                );
                toast.error(
                    t('bugReports.form.uploadFailed', 'Failed to upload "{{name}}".', {
                        name: file.name,
                    }),
                );
            }
        },
        [t],
    );

    const addFiles = useCallback(
        (files: FileList | File[]) => {
            const pending: IPendingBugReportAttachment[] = [];
            const toUpload: Array<{ localId: string; file: File }> = [];
            let count = items.length;

            for (const file of Array.from(files)) {
                if (count >= MAX_BUG_REPORT_ATTACHMENTS) {
                    toast.error(
                        t('bugReports.form.tooManyFiles', 'You can attach up to {{count}} files', {
                            count: MAX_BUG_REPORT_ATTACHMENTS,
                        }),
                    );
                    break;
                }
                if (file.size > MAX_BUG_REPORT_ATTACHMENT_SIZE_BYTES) {
                    toast.error(
                        t(
                            'bugReports.form.fileTooLarge',
                            '"{{name}}" is larger than {{size}} MB and was skipped',
                            { name: file.name, size: MAX_BUG_REPORT_ATTACHMENT_SIZE_MB },
                        ),
                    );
                    continue;
                }
                const localId = crypto.randomUUID();
                pending.push({
                    localId,
                    fileName: file.name,
                    contentType: file.type || FALLBACK_CONTENT_TYPE,
                    sizeBytes: file.size,
                    status: 'uploading',
                    progress: 0,
                });
                toUpload.push({ localId, file });
                count++;
            }

            if (pending.length === 0) return;
            setItems((prev) => [...prev, ...pending]);
            // Fired together rather than awaited in sequence: dropping three
            // screenshots at once should upload three bars, not one at a time.
            toUpload.forEach(({ localId, file }) => void uploadOne(localId, file));
        },
        [items.length, t, uploadOne],
    );

    const remove = useCallback((localId: string) => {
        setItems((prev) => prev.filter((item) => item.localId !== localId));
    }, []);

    const clear = useCallback(() => {
        setItems([]);
    }, []);

    const isUploading = items.some((item) => item.status === 'uploading');

    const uploaded: IBugReportAttachmentInput[] = items
        .filter((item): item is IPendingBugReportAttachment & { storageKey: string } =>
            Boolean(item.status === 'done' && item.storageKey),
        )
        .map((item) => ({
            storageKey: item.storageKey,
            fileName: item.fileName,
            contentType: item.contentType,
        }));

    return { items, uploaded, isUploading, addFiles, remove, clear };
}
